import { NavLink } from "react-router-dom";
import DeskTip from "@/components/common/DeskTip";
import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { createPortal } from "react-dom";
import { ListTodo, FileText, Lock, ArrowUpDown, GripVertical } from "lucide-react";
import Logo from "@/components/Logo";
import { ModuleTile, TileLabel, TILE } from "@/components/sidebar/ModuleTile";

/** Couleur claire du module (nom actif). */
const MODULE_LIGHT = { dashboard: "#7fb4ec", tasks: "#f3cf6b", documents: "#8db4ec", avi: "#f0a66e", crm: "#5fd2a8", tresorerie: "#5fd2b8", pointage: "#7fd6bf", settings: "#c9c9d1", calendar: "#cfa6f0" };
import AviIcon from "@/components/icons/AviIcon";
import SidebarBadgePopover from "@/components/SidebarBadgePopover";
import { useAuth } from "@/context/AuthContext";
import { hasRight } from "@/components/crm/crmShared";
import { ACCESS_DENIED_MSG } from "@/components/RightsRoute";
import { toast } from "@/lib/toast";
import { moduleGradient, MODULE_GRADIENTS } from "@/lib/moduleColors";
import api, { dedupedGet } from "@/lib/api";
import { useSidebarData } from "@/lib/sidebarData";
import { R2Button } from "@/components/crm/DeskConfirm";
import zohoMailIcon from "@/assets/zoho-mail.svg";
import { openZohoConnect, listenZohoConnected } from "@/lib/zohoConnect";
import {
  LayoutDashboard,
  Handshake,
  Landmark,
  Fingerprint,
} from "lucide-react";

/**
 * Sidebar — fixed left-hand dock.
 *
 * Each module tile is a **42×42** rounded rectangle (radius 11) painted with
 * the module's diagonal gradient (see `moduleGradient()` in
 * `/lib/moduleColors.js`). Icons are pure SVG (lucide stroke-based) drawn in
 * white with a `strokeWidth={2}` — thin & legible even at small sizes.
 *
 *   • Uniform tile size across every module (spec: 42×42 px, radius 11 px)
 *   • Drop shadow 0 3px 10px rgba(0,0,0,0.3) for depth on the dark bg
 *   • Label below the tile, small, centered
 *   • Performance ring pinned at the bottom (outside the flex distribution)
 */
const NAV = [
  { to: "/tableau-de-bord", label: "Tableau de bord", short: "Dashboard",   Icon: LayoutDashboard, key: "dashboard", gradient: "dashboard",   testid: "nav-dashboard",   badge: 0 },
  { to: "/taches",          label: "Tâches",          short: "Tâches",      Icon: ListTodo,        key: "tasks", gradient: "tasks",       testid: "nav-tasks",       badge: 0 },
  { to: "/documents",       label: "Documents",       short: "Documents",   Icon: FileText,        key: "documents", gradient: "documents",   testid: "nav-documents",   badge: 0 },
  // Spec §1 (rev-avi-1, 2026-02-07) — module AVI (Accusé · Validation ·
  // Information) fusionne les anciens modules Validations + Accusés.
  // L'icône est un mini triptyque de pilules colorées reprenant les 3
  // sous-types. Le badge est user-scoped (§4.10 : décideur en attente).
  { to: "/avi",             label: "AVI",             short: "AVI",         Icon: AviIcon,         key: "avi", gradient: "documents",   testid: "nav-avi",         badge: 0, isAvi: true },
  { to: "/crm",             label: "Commercial",      short: "Commercial",         Icon: Handshake,       key: "crm", gradient: "crm",         testid: "nav-crm",         badge: 0 },
  { to: "/tresorerie",      label: "Trésorerie",      short: "Trésorerie",  Icon: Landmark,        key: "tresorerie", gradient: "tresorerie",  testid: "nav-tresorerie",  badge: 0 },
  { to: "/pointage",        label: "Pointage",        short: "Pointage",    Icon: Fingerprint,     key: "pointage", gradient: "pointage",    testid: "nav-pointage",    badge: 0 },
];

function NotificationBadge({ count, testid, pulse, title, variant = "danger", onMouseEnter }) {
  if (!count || count < 1) return null;
  const label = count > 99 ? "99+" : String(count);
  // Spec iter147 point 2 (Feb 2026) — variant "todo" = badge bleu clignotant
  // permanent utilisé pour l'icône AVI (« Ma to-do AVI »).
  const isTodo = variant === "todo";
  return (
    <DeskTip text={title} side="right">
    <span
      className={`absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#141414] ${
        isTodo ? "avi-todo-badge" : "bg-status-danger"
      } ${pulse && !isTodo ? "avi-badge-pop" : ""}`}
      style={isTodo ? { background: "#3B82F6" } : undefined}
      onMouseEnter={onMouseEnter}
      data-testid={testid}
    >
      {label}
    </span>
    </DeskTip>
  );
}

/** Petite pastille bleue « messages non lus » (bas-droite). Distincte du
 *  badge de décisions/tâches (rouge, haut-droite) — voir spec post-prod
 *  rev-3, point 1. Icône bulle SVG minimaliste.
 *  Spec iter132 pt.9 (Feb 2026) — ``title`` propre au badge pour que
 *  chaque pastille (jaune tâches en attente / bleue messages non lus)
 *  ait son propre tooltip natif au survol, sans interférence entre les
 *  deux. */
function MessageBadge({ count, testid, pulse, icon = "message", title, onMouseEnter, onMouseLeave }) {
  if (!count || count < 1) return null;
  const label = count > 9 ? "9+" : String(count);
  // icon = "message" (bulle bleue) | "alert" (triangle orange).
  const color = icon === "alert" ? "#F97316" : "#3B82F6";
  const shadowRgba = icon === "alert" ? "rgba(249,115,22,0.5)" : "rgba(59,130,246,0.5)";
  return (
    <DeskTip text={title} side="right">
    <span
      className={`absolute -bottom-1.5 -right-1.5 h-[18px] min-w-[18px] px-1 rounded-full flex items-center justify-center ring-2 ring-[#141414] text-white text-[9px] font-bold ${pulse ? "avi-badge-pop" : ""}`}
      style={{ background: color, boxShadow: `0 2px 6px ${shadowRgba}` }}
      data-testid={testid}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {icon === "alert" ? (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3 L22 20 L2 20 Z" fill="#FFFFFF" />
          <rect x="11" y="9" width="2" height="6" fill="#F97316" />
          <rect x="11" y="16.5" width="2" height="2" fill="#F97316" />
        </svg>
      ) : (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z"
            fill="#FFFFFF" stroke="#FFFFFF" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      )}
      <span className="ml-0.5">{label}</span>
    </span>
    </DeskTip>
  );
}

/** iter373 pt 1 — tuile « E-mail » : Zoho Mail dans une fenêtre séparée nommée (un 2e clic la ramène au premier plan).
 *  iter374 pt 1 : logo à la taille du symbole Dashboard (Lucide 22 px, boîte 22 × 22) · pt 2 : pastille = non lus Zoho (OAuth, toutes les 2 min). */
function EmailTile() {
  const winRef = useRef(null);
  const [zoho, setZoho] = useState({ connected: null, unread: null, configured: true });
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try { const { data } = await dedupedGet("/zoho/unread"); if (!stop) setZoho((z) => ({ ...z, connected: data.connected, unread: data.unread ?? (data.stale ? z.unread : null) })); }
      catch { if (!stop) setZoho((z) => ({ ...z, connected: false, unread: z.unread })); }
    };
    const status = () => api.get("/zoho/status").then(({ data }) => { if (!stop) setZoho((z) => ({ ...z, configured: data.configured, connected: data.connected && !data.needs_reconnect })); if (data.connected && !data.needs_reconnect) tick(); }).catch(() => {});
    status();
    listenZohoConnected();
    const id = setInterval(tick, 120000);
    const onChanged = () => status();
    window.addEventListener("desk:zoho-changed", onChanged);
    return () => { stop = true; clearInterval(id); window.removeEventListener("desk:zoho-changed", onChanged); };
  }, []);
  // Complément pt 2 — boîte non connectée (ou reconnexion requise) : carte Le Desk au clic ; « Plus tard » = 24 h de répit.
  const [askConnect, setAskConnect] = useState(false);
  const openMail = () => {
    if (winRef.current && !winRef.current.closed) { winRef.current.focus(); return; }
    winRef.current = window.open("https://mail.zoho.com/", "ledesk_zoho_mail", "popup=yes,width=1200,height=800,noopener=no");
    winRef.current?.focus();
  };
  const connect = async () => {
    setAskConnect(false);
    try { await openZohoConnect(); } catch { openMail(); }
  };
  const later = () => { localStorage.setItem("ledesk_zoho_ask_later", String(Date.now())); setAskConnect(false); openMail(); };
  const openZoho = () => {
    const snoozed = Date.now() - Number(localStorage.getItem("ledesk_zoho_ask_later") || 0) < 86400000;
    if (zoho.configured && zoho.connected === false && !snoozed) { setAskConnect(true); return; }
    openMail();
  };
  return (
    <div className="relative w-full h-full flex items-center justify-center" data-testid="nav-email-cell">
      <Dialog open={askConnect} onOpenChange={setAskConnect}>
        <DialogContent className="max-w-sm" data-testid="zoho-ask-connect">
          <DialogHeader><DialogTitle className="flex items-center" style={{ gap: 8, fontSize: 14 }}><span className="flex items-center justify-center" style={{ width: 26, height: 26, borderRadius: 7, background: "#ffffff" }}><img src={zohoMailIcon} alt="" width={16} height={16} /></span>Connecter ma boîte Zoho pour voir vos non lus</DialogTitle></DialogHeader>
          <p style={{ fontSize: 11.5, color: "#9a9aa3", margin: 0 }}>Le Desk lit uniquement le nombre de non lus de votre boîte de réception — jamais le contenu.</p>
          <div className="flex items-center justify-end" style={{ gap: 8, marginTop: 6 }}>
            <button type="button" onClick={later} style={{ fontSize: 12, padding: "7px 14px", borderRadius: 20, border: "0.5px solid #34343c", color: "#c9c9d1", background: "transparent" }} data-testid="zoho-ask-later">Plus tard</button>
            <button type="button" onClick={connect} style={{ fontSize: 12, padding: "7px 16px", borderRadius: 20, background: "#7a6cf0", color: "#fff", fontWeight: 600, border: "none" }} data-testid="zoho-ask-connect-btn">Connecter</button>
          </div>
        </DialogContent>
      </Dialog>
      <button type="button" onClick={openZoho} title={zoho.unread ? `E-mail · ${zoho.unread} non lu(s)` : "E-mail (Zoho Mail)"} className={`w-full h-full group flex flex-col items-center justify-center ${TILE.gap} rounded-lg py-2 transition-colors hover:bg-white/5`} style={{ background: "transparent", border: "none" }} data-testid="nav-email">
        <ModuleTile img={zohoMailIcon} background="#ffffff" testId="nav-email-tile">
          <NotificationBadge count={zoho.unread || 0} testid="nav-email-badge" variant="danger" title={zoho.unread ? `${zoho.unread} e-mail(s) non lu(s)` : undefined} />
        </ModuleTile>
        <TileLabel color="#9a988f" testId="nav-email-label">E-mail</TileLabel>
      </button>
    </div>
  );
}

export default function Sidebar() {
  const { user } = useAuth();
  // iter418 pt 2 — badge « TEST » : nom d'hôte preview/local OU `GET /api/env` → is_preview (APP_ENV=preview). Jamais en production.
  const [envPreview, setEnvPreview] = useState(false);
  useEffect(() => { dedupedGet("/env").then(({ data }) => setEnvPreview(!!(data || {}).is_preview)).catch(() => {}); }, []);
  const showEmailTile = !!(user?.zoho_email || "").trim();
  const [aviPulse, setAviPulse] = useState(false);
  const prevAviRef = useRef(null);
  const pulseTimeoutRef = useRef(null);

  // iter395 pt 1 — pastilles = calcul serveur unique (/badges), non vus seulement ; pt 2 — ordre des modules (/sidebar/order).
  const { badges, order, setOrder, refresh } = useSidebarData(user?.id);
  const [hoverKey, setHoverKey] = useState(null); // "/avi" | "/taches"
  const unreadTasks = { waiting: badges.tasks.waiting || [], to_verify: badges.tasks.to_verify || [] };
  const unreadAvi = badges.avi.items || [];
  const taskBadge = badges.tasks.count || 0;
  const aviBadge = badges.avi.count || 0;

  // iter395 pt 2 — mode « Réorganiser » (Premier Admin).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(order);
  const [saving, setSaving] = useState(false);
  const dragRef = useRef(null);
  const startEdit = () => { setDraft(order); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(order); };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/sidebar/order", { order: draft });
      setOrder(data.order); setEditing(false);
      toast.success("Ordre enregistré", { description: "Barre latérale · appliqué à tous les utilisateurs" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); cancelEdit(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const moveTo = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setDraft((d) => { const n = d.filter((k) => k !== fromKey); n.splice(n.indexOf(toKey), 0, fromKey); return n; });
  };

  const hoverTimerRef = useRef(null);

  // Hausse du compteur AVI → rebond de la pastille (la première valeur ne déclenche rien).
  useEffect(() => {
    if (prevAviRef.current === null) { prevAviRef.current = aviBadge; return; }
    if (aviBadge > prevAviRef.current) {
      setAviPulse(true);
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = setTimeout(() => setAviPulse(false), 1800);
    }
    prevAviRef.current = aviBadge;
  }, [aviBadge]);

  useEffect(() => {
    const onPulse = () => {
      setAviPulse(true);
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = setTimeout(() => setAviPulse(false), 1800);
      refresh();
    };
    window.addEventListener("avi:badge-pulse", onPulse);
    return () => { window.removeEventListener("avi:badge-pulse", onPulse); if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current); };
  }, [refresh]);

  // LOT 4 — un module sans droit « Voir » RESTE visible mais verrouillé (cadenas, non navigable). Dashboard jamais verrouillé.
  const MOD = { "/documents": "documents", "/taches": "tasks", "/avi": "avi", "/crm": "crm", "/tresorerie": "tresorerie", "/pointage": "pointage" };
  const canView = (n) => { const m = MOD[n.to]; return !m || hasRight(user, `${m}.view`); };
  const activeOrder = editing ? draft : order;
  const items = [...NAV]
    .sort((a, b) => activeOrder.indexOf(a.key) - activeOrder.indexOf(b.key))
    .map((n) => ({ ...n, locked: !canView(n) }))
    .map((n) => {
      if (n.locked) return { ...n, badge: 0, msgBadge: 0 };
      if (n.to === "/taches") return { ...n, badge: taskBadge, msgBadge: 0 };
      if (n.to === "/avi") return { ...n, badge: aviBadge, pulse: aviPulse, msgBadge: 0 };
      return { ...n, badge: 0, msgBadge: 0 }; // iter395 — aucune pastille sur Documents, Commercial, Trésorerie, Pointage
    });

  // Compose popover sections per module.
  const popoverSectionsFor = (to) => {
    if (to === "/avi") {
      return [
        {
          key: "waiting_me", label: "Décisions en attente", color: "#EC4899",
          icon: SidebarBadgePopover.Icons.Waiting,
          items: unreadAvi.map((it) => ({
            id: it.id, ref: it.reference, title: it.subject,
            priority: it.priority, href: `/avi/${it.id}`, accent: "#EC4899",
          })),
        },
      ];
    }
    if (to === "/taches") {
      // Spec iter169 (2026-02) — la ligne de meta affiche la date à
      // laquelle la tâche a été assignée au user courant (l'assignment
      // "waiting/redo" en cours). Fallback : t.created_at.
      const fmtDate = (iso) => {
        if (!iso) return null;
        try {
          const d = new Date(iso);
          if (Number.isNaN(d.getTime())) return null;
          return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
        } catch { return null; }
      };
      return [
        {
          key: "waiting", label: "Tâches en attente", color: "#F9BC26",
          icon: SidebarBadgePopover.Icons.Waiting, total: unreadTasks.waiting.length,
          items: unreadTasks.waiting.slice(0, 8).map((t) => {
            const mine = (t.assignments || []).find(
              (a) => a.user_id === user?.id && (a.status === "waiting" || a.status === "redo"),
            );
            const dateStr = fmtDate(mine?.assigned_at || t.created_at);
            return {
              id: t.id, ref: t.reference || t.short_ref, title: t.subject || t.title,
              priority: t.priority,
              meta: dateStr ? `Assignée le ${dateStr}` : null,
              href: `/taches/${t.id}`, accent: "#F9BC26",
            };
          }),
        },
        {
          key: "to_verify", label: "À vérifier", color: "#3B82F6",
          icon: SidebarBadgePopover.Icons.Task, total: unreadTasks.to_verify.length,
          items: unreadTasks.to_verify.slice(0, 8).map((t) => ({
            id: t.id, ref: t.reference || t.short_ref, title: t.subject || t.title,
            meta: fmtDate(t.updated_at || t.created_at) ? `Prête à vérifier · ${fmtDate(t.updated_at || t.created_at)}` : null,
            href: `/taches/${t.id}`, accent: "#3B82F6",
          })),
        },
      ];
    }
    return [];
  };

  // Les popovers sont rendus dans un portail (position fixe) : la barre
  // latérale flottante a `overflow: hidden` et les rognait sinon.
  const [hoverPos, setHoverPos] = useState({ top: 0, left: 96 });
  const itemRefs = useRef({});
  const openHover = (to) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    const base = to.replace(":msg", "");
    const r = itemRefs.current[base]?.getBoundingClientRect();
    if (r) setHoverPos({ top: r.top, left: r.right });
    setHoverKey(to);
  };
  const closeHover = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    // Petit délai pour permettre au curseur de traverser le gap vers le popover.
    hoverTimerRef.current = setTimeout(() => setHoverKey(null), 180);
  };

  return (
    <aside
      className="fixed app-sidebar-w flex flex-col app-sidebar-float"
      style={{ left: 8, top: 8, bottom: 8, background: "#141418", border: "0.5px solid #26262e", borderRadius: 14, zIndex: 100, overflowY: "auto", overflowX: "hidden" }}
      data-testid="app-sidebar"
    >
      {/* Spec rev164 (2026-02) — grille à hauteur égale sur toute la
          colonne : 1 rangée logo · 6 rangées modules · 1 rangée
          « Mon indice ». Chaque cellule est centrée verticalement et
          horizontalement dans sa ligne. */}
      <nav
        className="h-full grid p-2"
        style={{
          gridTemplateRows: `1fr auto repeat(${items.length + (showEmailTile ? 1 : 0)}, 1fr) ${editing ? "auto" : "1fr"}`,
          alignItems: "center",
          justifyItems: "center",
        }}
        data-testid="sidebar-nav"
      >
        {/* Logo cell — spec 2026-08-02 points #3+#4 : fond aligné sur l'app, taille agrandie */}
        <div
          className="flex flex-col items-center justify-center gap-1.5 py-2 relative w-full h-full"
          data-testid="sidebar-logo-cell"
        >
          <Logo size={52} />
          <span className="text-[13px] font-semibold text-white text-center leading-tight">
            Le Desk
          </span>
          {/* Spec rev111 (2026-02) — badge "TEST" visible UNIQUEMENT sur
              l'env preview (hostname finissant en `.preview.emergentagent.com`)
              ou en local (`localhost` / `127.0.0.1`). Jamais rendu sur
              vert-tec.com — condition évaluée au runtime pour survivre
              à un même bundle déployé sur plusieurs domaines. */}
          {(() => {
            if (typeof window === "undefined") return null;
            const h = window.location.hostname || "";
            const isPreview =
              envPreview ||
              h.endsWith(".preview.emergentagent.com") ||
              h === "localhost" ||
              h === "127.0.0.1";
            if (!isPreview) return null;
            return (
              <DeskTip text="Environnement preview — pas la production" side="right">
              <span
                className="mt-0.5 inline-flex items-center justify-center rounded font-bold tracking-widest"
                style={{
                  fontSize: 9.5,
                  padding: "1px 6px",
                  color: "#F97316",
                  border: "1px solid #F97316",
                  background: "transparent",
                  letterSpacing: "0.10em",
                }}
                data-testid="env-badge-test"
              >
                TEST
              </span>
              </DeskTip>
            );
          })()}
        </div>

        {/* Séparateur logo — 2 traits + point (chantier 4b) */}
        <div className="flex items-center justify-center w-full" style={{ padding: "10px 0" }} aria-hidden="true" data-testid="sidebar-logo-separator">
          <span style={{ width: 56, height: 1, background: "linear-gradient(90deg, transparent, #3a3a44, transparent)" }} />
        </div>

        {/* Module cells */}
        {items.map(({ to, short, Icon, key: modKey, gradient, testid, badge, pulse, msgBadge, msgPulse: mPulse, msgIcon, isAvi, locked }, modIdx) => {
          const dragProps = editing ? {
            draggable: true,
            onDragStart: (e) => { dragRef.current = modKey; e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", modKey); } catch { /* noop */ } },
            onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
            onDragEnter: (e) => { e.preventDefault(); moveTo(dragRef.current, modKey); },
            onDrop: (e) => { e.preventDefault(); moveTo(dragRef.current, modKey); dragRef.current = null; },
            onDragEnd: () => { dragRef.current = null; },
            style: { cursor: "grab" },
            "data-testid": `sidebar-drag-${modKey}`,
          } : {};
          const wobble = editing ? { animation: `sidebar-wobble 0.5s ease-in-out infinite ${modIdx % 2 ? "alternate-reverse" : "alternate"}` } : undefined;
          const grip = editing ? <span className="absolute" style={{ left: 2, top: "50%", transform: "translateY(-50%)", color: "#6E6E78", display: "inline-flex" }} aria-hidden="true" data-testid={`sidebar-grip-${modKey}`}><GripVertical size={14} /></span> : null;
          const hoverable = !locked && !editing && ["/avi", "/taches"].includes(to);
          const hasBadge = (badge || 0) + (msgBadge || 0) > 0;
          if (locked) return (
            <div key={to} className="relative w-full h-full flex items-center justify-center" data-testid={`${testid}-locked`} {...dragProps}>
              {grip}
              <DeskTip text="Accès non autorisé" side="right">
                <button type="button" onClick={() => editing ? null : toast.error(ACCESS_DENIED_MSG, { id: "access-denied" })} className={`w-full h-full flex flex-col items-center justify-center ${TILE.gap} rounded-lg py-2`} style={{ cursor: editing ? "grab" : "not-allowed", background: "transparent", border: "none" }} aria-disabled="true" data-testid={testid}>
                  <div className="relative" style={wobble}>
                    <ModuleTile icon={Icon} background={moduleGradient(gradient)} boxShadow="none" opacity={0.55} testId={`${testid}-tile`} />
                    <span className="absolute inline-flex items-center justify-center" style={{ top: -5, right: -5, width: 15, height: 15, borderRadius: 4, background: "#1c1c20", border: "0.5px solid #3a3a40" }} data-testid={`${testid}-lock`}><Lock size={9} style={{ color: "#8b8b93" }} /></span>
                  </div>
                  <TileLabel color="#8b8b93" testId={`${testid}-label`} className="group-hover:text-[#8b8b93]">{short}</TileLabel>
                </button>
              </DeskTip>
            </div>
          );
          return (<Fragment key={to}>
          <div
            ref={(el) => { itemRefs.current[to] = el; }}
            className="relative w-full h-full flex items-center justify-center"
            onMouseEnter={() => hoverable && openHover(to)}
            onMouseLeave={() => hoverable && closeHover()}
            {...dragProps}
          >
          {grip}
          <NavLink
            to={to}
            onClick={(e) => { if (editing) { e.preventDefault(); return; } try { Object.keys(sessionStorage).filter((k) => k.startsWith("ledesk:search:") || k === "ledesk:scroll").forEach((k) => sessionStorage.removeItem(k)); } catch { /* noop */ } }}
            data-testid={testid}
            title={short}
            className={({ isActive }) =>
              `w-full h-full group flex flex-col items-center justify-center ${TILE.gap} rounded-lg py-2 transition-colors ${
                isActive ? "" : "hover:bg-white/5"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className="relative" style={wobble}>
                  {isActive && <span className="absolute" style={{ left: -14, top: 4, width: 2, height: 34, borderRadius: 2, background: (MODULE_GRADIENTS[gradient] || {}).from || "#4fc79c" }} data-testid={`${testid}-active-bar`} />}
                  <ModuleTile icon={Icon} background={moduleGradient(gradient)} testId={`${testid}-tile`} onMouseEnter={hoverable ? () => openHover(to) : undefined}
                    boxShadow={isActive ? `0 0 0 2px #141418, 0 0 0 3.5px ${(MODULE_GRADIENTS[gradient] || {}).from || "#4fc79c"}` : TILE.shadow} />
                  <NotificationBadge
                    count={badge}
                    testid={`${testid}-badge`}
                    pulse={pulse}
                    variant={isAvi ? "todo" : "danger"}
                    onMouseEnter={hoverable ? () => openHover(to) : undefined}
                    title={
                      to === "/taches" ? (badge === 1 ? "1 tâche en attente" : `${badge} tâches en attente`) :
                      to === "/avi"    ? (badge === 1 ? "1 élément dans Ma to-do AVI" : `${badge} éléments dans Ma to-do AVI`) :
                      to === "/documents" ? (badge === 1 ? "1 étape à vérifier" : `${badge} étapes à vérifier`) :
                      undefined
                    }
                  />
                  <MessageBadge
                    count={msgBadge}
                    testid={`${testid}-msg-badge`}
                    pulse={mPulse}
                    icon={msgIcon || "message"}
                    title={
                      to === "/taches" ? (msgBadge === 1 ? "1 message non lu" : `${msgBadge} messages non lus`) :
                      to === "/avi"    ? (msgBadge === 1 ? "1 message AVI non lu" : `${msgBadge} messages AVI non lus`) :
                      to === "/documents" ? (msgBadge === 1 ? "1 étape à revalider" : `${msgBadge} étapes à revalider`) :
                      undefined
                    }
                    onMouseEnter={to !== "/documents" ? () => openHover(`${to}:msg`) : undefined}
                  />
                </div>
                <TileLabel color={isActive ? MODULE_LIGHT[gradient] || "#f2f2f5" : "#9a988f"} bold={isActive} testId={`${testid}-label`}>{short}</TileLabel>
              </>
            )}
          </NavLink>
          {hoverable && (hoverKey === to || hoverKey === `${to}:msg`) && createPortal(
            <div onMouseEnter={() => hoverKey && openHover(hoverKey)} onMouseLeave={closeHover} style={{ position: "fixed", left: hoverPos.left, top: hoverPos.top, width: 0, height: 0, zIndex: 9999 }}>
              <SidebarBadgePopover
                visible={hoverKey === to}
                title={to === "/avi" ? "Ma to-do AVI" : to === "/documents" ? "Étapes à vérifier" : "Tâches en attente"}
                sections={popoverSectionsFor(to)}
                moduleColor={
                  to === "/avi" ? "#2a9a7a"
                  : to === "/documents" ? "#2B579A"
                  : "#F9BC26"
                }
              />
            </div>,
            document.body,
          )}
          </div>
          {to === "/tableau-de-bord" && showEmailTile && <EmailTile />}
          </Fragment>);
        })}

        {/* iter395 pt 2 — pied : « Réorganiser » (Premier Admin) ou Enregistrer / Annuler (mode édition). */}
        <div className="w-full h-full flex flex-col items-center justify-end" style={{ paddingBottom: 6, gap: 6 }} data-testid="sidebar-perf-cell">
          <style>{`@keyframes sidebar-wobble{from{transform:rotate(-2deg)}to{transform:rotate(2deg)}}`}</style>
          {editing ? (
            <div className="flex flex-col w-full" style={{ gap: 4, padding: "0 3px" }} data-testid="sidebar-reorder-actions">
              <R2Button tone="blue" onClick={saveEdit} disabled={saving} testId="sidebar-reorder-save" style={{ height: 24, fontSize: 10, padding: "0 4px", borderRadius: 7, width: "100%" }}>Enregistrer</R2Button>
              <R2Button tone="grey" onClick={cancelEdit} disabled={saving} testId="sidebar-reorder-cancel" style={{ height: 24, fontSize: 10, padding: "0 4px", borderRadius: 7, width: "100%" }}>Annuler</R2Button>
            </div>
          ) : user?.is_first_admin ? (
            <button type="button" onClick={startEdit} className="flex flex-col items-center" style={{ gap: 2, background: "transparent", border: "none", color: "#8A8A96", cursor: "pointer", padding: 4 }} title="Réorganiser les modules" data-testid="sidebar-reorder-btn">
              <ArrowUpDown size={16} />
              <span style={{ fontSize: 9.5, lineHeight: 1 }}>Réorganiser</span>
            </button>
          ) : null}
        </div>
      </nav>
    </aside>
  );
}
